import React, { useState } from 'react';
import { getApiBaseUrl } from '../../../config/oncorisk-config.js';
import './Api.css';

const Api = ({ cookieId }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [osType, setOsType] = useState('windows');

  const baseUrl = getApiBaseUrl();

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const searchExamples = {
    windows: [
      {
        title: 'Search for a Gene',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/search?term=BRCA1" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Search Specific Types',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/search?term=BRCA1&types=features,variants" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Search for Diseases',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/search?term=lung&types=diseases" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Search for Therapies',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/search?term=imatinib&types=therapies" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      }
    ],
    linux: [
      {
        title: 'Search for a Gene',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/search?term=BRCA1"`
      },
      {
        title: 'Search Specific Types',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/search?term=BRCA1&types=features,variants"`
      },
      {
        title: 'Search for Diseases',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/search?term=lung&types=diseases"`
      },
      {
        title: 'Search for Therapies',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/search?term=imatinib&types=therapies"`
      }
    ]
  };

  const detailExamples = {
    windows: [
      {
        title: 'Get Feature Details',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/items/features/6" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Get Variant Details',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/items/variants/1" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Get Therapy Details',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/items/therapies/123" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      },
      {
        title: 'Get Disease Details',
        code: `Invoke-RestMethod -Uri "${baseUrl}/api/v1/items/diseases/456" \`
  -Headers @{ "X-API-Key" = "${cookieId}" }`
      }
    ],
    linux: [
      {
        title: 'Get Feature Details',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/items/features/6"`
      },
      {
        title: 'Get Variant Details',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/items/variants/1"`
      },
      {
        title: 'Get Therapy Details',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/items/therapies/123"`
      },
      {
        title: 'Get Disease Details',
        code: `curl -H "X-API-Key: ${cookieId}" \\
  "${baseUrl}/api/v1/items/diseases/456"`
      }
    ]
  };

  const searchResponse = {
    title: 'Search Response',
    code: `[
  {
    "id": 6,
    "name": "BRCA1",
    "itemType": "features",
    "itemTypeDisplayName": "Feature"
  },
  {
    "id": 4100,
    "name": "BRCA1 c.212+1G>T",
    "itemType": "molecularprofiles",
    "itemTypeDisplayName": "Molecular Profile"
  }
]`
  };

  const detailResponse = {
    title: 'Item Detail Response',
    code: `{
  "id": 6,
  "name": "BRCA1",
  "aliases": ["BRCC1", "FANCS", "RNF53"],
  "description": "BRCA1 DNA repair associated...",
  "entrez_id": 672,
  "associated_diseases": [...],
  "associated_therapies": [...],
  "associated_evidence": [...],
  ...
}`
  };

  const searchParams = [
    { field: 'term', type: 'string', required: 'Yes', description: 'Search query (min 1 character)' },
    { field: 'types', type: 'string', required: 'No', description: 'Comma-separated entity types to search (default: all)' }
  ];

  const detailParams = [
    { field: 'itemType', type: 'string', required: 'Yes', description: 'Entity type from search results (e.g., features, variants)' },
    { field: 'itemId', type: 'integer', required: 'Yes', description: 'Entity ID from search results' }
  ];

  const itemTypes = [
    { type: 'features', description: 'Genes, fusions, factors', fields: 'name, aliases, description, entrez_id' },
    { type: 'variants', description: 'Genetic variants', fields: 'name, hgvs_expressions, clinvar_entries, feature_id' },
    { type: 'molecularprofiles', description: 'Molecular profiles', fields: 'name, variant_ids, evidence_ids' },
    { type: 'diseases', description: 'Cancer/disease types', fields: 'name, doid, aliases' },
    { type: 'therapies', description: 'Drugs/treatments', fields: 'name, ncit_id, aliases' },
    { type: 'evidence', description: 'Literature evidence', fields: 'description, evidence_type, significance, source_id' },
    { type: 'assertions', description: 'Clinical assertions', fields: 'summary, assertion_type, amp_level, therapy_ids' },
    { type: 'phenotypes', description: 'Clinical phenotypes', fields: 'name, hpo_id' },
    { type: 'sources', description: 'Publications', fields: 'citation, title, author_string, pubmed_id' },
    { type: 'variantgroups', description: 'Variant groupings', fields: 'name, description' }
  ];

  const errorCodes = [
    { code: '400', description: 'Bad request (missing term, invalid itemType)' },
    { code: '404', description: 'Item not found' },
    { code: '500', description: 'Reference query could not be completed' }
  ];

  let copyIdx = 0;

  return (
    <div className="OCRP-api-container">
      <div className="OCRP-api-section">
        <h2>OncoRiskPro API Documentation</h2>
        <p className="OCRP-api-description">
          Access the OncoRisk knowledge base programmatically for integration with your clinical workflows and applications.
        </p>
      
      </div>

      <div className="OCRP-api-section">
        <h2>Authentication</h2>
        <p className="OCRP-api-description">
          Knowledge-base search and detail endpoints are public. The credential header shown
          below is accepted for compatibility and is required by the separate upload, job,
          and result lifecycle:
        </p>
        <pre className="OCRP-api-code OCRP-api-code-inline">X-API-Key: {cookieId}</pre>
      </div>

      <div className="OCRP-api-os-toggle">
        <span className="OCRP-api-os-label">Examples for:</span>
        <div className="OCRP-api-os-buttons">
          <button
            className={`OCRP-api-os-btn ${osType === 'windows' ? 'active' : ''}`}
            onClick={() => setOsType('windows')}
          >
            <i className="fab fa-windows"></i> Windows
          </button>
          <button
            className={`OCRP-api-os-btn ${osType === 'linux' ? 'active' : ''}`}
            onClick={() => setOsType('linux')}
          >
            <i className="fab fa-linux"></i> Linux / macOS
          </button>
        </div>
      </div>

      <div className="OCRP-api-section">
        <h2>Quick Search</h2>
        <p className="OCRP-api-endpoint">
          <span className="OCRP-method get">GET</span>
          <code>/api/v1/search</code>
        </p>
        <p className="OCRP-api-description">
          Search across the oncology knowledge base for features, variants, therapies, diseases, and more.
        </p>

        <h3>Query Parameters</h3>
        <table className="OCRP-api-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {searchParams.map((param, index) => (
              <tr key={index}>
                <td><code>{param.field}</code></td>
                <td>{param.type}</td>
                <td>{param.required}</td>
                <td>{param.description}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="OCRP-api-description" style={{ marginTop: '1.5vh' }}>
          <strong>Available Types:</strong> <code>features</code>, <code>variants</code>, <code>molecularprofiles</code>, <code>diseases</code>, <code>therapies</code>, <code>evidence</code>, <code>assertions</code>, <code>phenotypes</code>, <code>sources</code>, <code>variantgroups</code>
        </p>

        <h3>Examples</h3>
        <div className="OCRP-api-examples">
          {searchExamples[osType].map((example, index) => {
            const idx = copyIdx++;
            return (
              <div key={index} className="OCRP-api-example">
                <div className="OCRP-api-example-header">
                  <h4>{example.title}</h4>
                  <button
                    className={`OCRP-copy-btn ${copiedIndex === idx ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(example.code, idx)}
                  >
                    <i className={`fas ${copiedIndex === idx ? 'fa-check' : 'fa-copy'}`}></i>
                    {copiedIndex === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="OCRP-api-code">{example.code}</pre>
              </div>
            );
          })}
        </div>

        <h3>Response</h3>
        <div className="OCRP-api-response">
          <h4>{searchResponse.title}</h4>
          <pre className="OCRP-api-code">{searchResponse.code}</pre>
        </div>
      </div>

      <div className="OCRP-api-section">
        <h2>Item Detail</h2>
        <p className="OCRP-api-endpoint">
          <span className="OCRP-method get">GET</span>
          <code>/api/v1/items/:itemType/:itemId</code>
        </p>
        <p className="OCRP-api-description">
          Fetch full details for a specific entity with all relationships resolved.
        </p>

        <h3>Path Parameters</h3>
        <table className="OCRP-api-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {detailParams.map((param, index) => (
              <tr key={index}>
                <td><code>{param.field}</code></td>
                <td>{param.type}</td>
                <td>{param.required}</td>
                <td>{param.description}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>Examples</h3>
        <div className="OCRP-api-examples">
          {detailExamples[osType].map((example, index) => {
            const idx = copyIdx++;
            return (
              <div key={index} className="OCRP-api-example">
                <div className="OCRP-api-example-header">
                  <h4>{example.title}</h4>
                  <button
                    className={`OCRP-copy-btn ${copiedIndex === idx ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(example.code, idx)}
                  >
                    <i className={`fas ${copiedIndex === idx ? 'fa-check' : 'fa-copy'}`}></i>
                    {copiedIndex === idx ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="OCRP-api-code">{example.code}</pre>
              </div>
            );
          })}
        </div>

        <h3>Response</h3>
        <div className="OCRP-api-response">
          <h4>{detailResponse.title}</h4>
          <pre className="OCRP-api-code">{detailResponse.code}</pre>
        </div>
      </div>

      <div className="OCRP-api-section">
        <h2>Workflow</h2>
        <div className="OCRP-api-description">
          <ol style={{ margin: '1vh 0', paddingLeft: '2vh', lineHeight: '2' }}>
            <li><strong>Search</strong> — Call <code>/api/v1/search?term=...</code> to find entities</li>
            <li><strong>Select</strong> — Pick an item from results (note its <code>id</code> and <code>itemType</code>)</li>
            <li><strong>Detail</strong> — Call <code>/api/v1/items/{'{itemType}'}/{'{id}'}</code> to get full data with relationships</li>
          </ol>
        </div>
      </div>

      <div className="OCRP-api-section">
        <h2>Data Types Reference</h2>
        <table className="OCRP-api-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>Example Fields</th>
            </tr>
          </thead>
          <tbody>
            {itemTypes.map((item, index) => (
              <tr key={index}>
                <td><code>{item.type}</code></td>
                <td>{item.description}</td>
                <td><code>{item.fields}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="OCRP-api-section">
        <h2>Error Codes</h2>
        <table className="OCRP-api-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {errorCodes.map((error, index) => (
              <tr key={index}>
                <td><span className={`OCRP-status-code code-${error.code}`}>{error.code}</span></td>
                <td>{error.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Api;
